import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaHeart,
  FaQuoteLeft,
  FaCheckCircle,
  FaShieldAlt,
  FaRocket,
  FaUsers,
  FaBook,
  FaDownload,
  FaStar,
  FaTrendingUp,
  FaAward,
  FaGraduationCap,
  FaLightbulb,
  FaHandsHelping,
} from "react-icons/fa";
import {
  FiUsers,
  FiBook,
  FiDownload,
  FiStar,
  FiTrendingUp,
  FiAward,
} from "react-icons/fi";
import { HiOutlineSparkles } from "react-icons/hi";
import axios from "axios";
import Loader from "../components/common/Loader";

const About = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalNotes: 0,
    totalDownloads: 0,
    totalLikes: 0,
    platformRating: 0,
  });
  const [loading, setLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    fetchStats();
    checkDarkMode();
    checkMobile();
  }, []);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);

    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const checkMobile = () => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get("/api/stats");

      const notesResponse = await axios.get(
        "/api/notes?limit=1000",
      );
      const totalLikes =
        notesResponse.data.notes?.reduce(
          (sum, note) => sum + (note.likes?.length || 0),
          0,
        ) || 0;

      const ratingResponse = await axios.get(
        "/api/ratings/platform",
      );

      setStats({
        totalUsers: response.data.totalUsers || 0,
        totalNotes: response.data.totalNotes || 0,
        totalDownloads: response.data.totalDownloads || 0,
        totalLikes: totalLikes,
        platformRating: ratingResponse.data.averageRating || 0,
      });
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      setStats({
        totalUsers: 0,
        totalNotes: 0,
        totalDownloads: 0,
        totalLikes: 0,
        platformRating: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === 0) return "0";
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M+";
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K+";
    }
    return num.toString();
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";

  const stats_data = [
    {
      icon: <FiUsers size={32} strokeWidth={1.5} />,
      value: formatNumber(stats.totalUsers),
      label: "Active Students",
      description: "Growing community of learners",
      gradient: "linear-gradient(135deg, #06b6d4, #0891b2)",
    },
    {
      icon: <FiBook size={32} strokeWidth={1.5} />,
      value: formatNumber(stats.totalNotes),
      label: "Study Materials",
      description: "Comprehensive notes & resources",
      gradient: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
    },
    {
      icon: <FiDownload size={32} strokeWidth={1.5} />,
      value: formatNumber(stats.totalDownloads),
      label: "Resource Downloads",
      description: "Knowledge shared globally",
      gradient: "linear-gradient(135deg, #10b981, #059669)",
    },
    {
      icon: <FiStar size={32} strokeWidth={1.5} />,
      value: stats.platformRating > 0 ? stats.platformRating.toFixed(1) : "0",
      label: "Platform Rating",
      description: "Student satisfaction",
      gradient: "linear-gradient(135deg, #f59e0b, #d97706)",
    },
  ];

  const values = [
    {
      icon: <FiTrendingUp size={28} strokeWidth={1.5} />,
      title: "Quality First",
      description: "Curated and verified content",
      gradient: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
    },
    {
      icon: <FiUsers size={28} strokeWidth={1.5} />,
      title: "Community Driven",
      description: "Built by students, for students",
      gradient: "linear-gradient(135deg, #06b6d4, #0891b2)",
    },
    {
      icon: <FiAward size={28} strokeWidth={1.5} />,
      title: "Quick Access",
      description: "Quick access to notes & tools",
      gradient: "linear-gradient(135deg, #10b981, #059669)",
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div
      style={{
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Animated Background Particles */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{
          x: [0, -100, 0],
          y: [0, 80, 0],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          bottom: "10%",
          right: "-5%",
          width: "350px",
          height: "350px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Hero Section - Purple/Blue Gradient */}
      <section
        style={{
          background:
            "linear-gradient(135deg, #1e1b4b 0%, #2e1065 50%, #1e1b4b 100%)",
          color: "white",
          padding: isMobile ? "4rem 1.5rem" : "6rem 2rem",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.1, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity }}
          style={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            background:
              "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
            borderRadius: "50%",
          }}
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.1, 0.2],
          }}
          transition={{ duration: 10, repeat: Infinity, delay: 2 }}
          style={{
            position: "absolute",
            bottom: -150,
            left: -150,
            width: 400,
            height: 400,
            background:
              "radial-gradient(circle, rgba(59,130,246,0.2) 0%, transparent 70%)",
            borderRadius: "50%",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 300 }}
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            style={{ display: "inline-block", marginBottom: "1rem" }}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.2)",
                width: "80px",
                height: "80px",
                borderRadius: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
              }}
            >
              <HiOutlineSparkles size={40} color="white" />
            </div>
          </motion.div>
          <h1
            style={{
              fontSize: isMobile ? "2rem" : "3rem",
              marginBottom: "1rem",
              fontWeight: "bold",
              background: "linear-gradient(135deg, #fff, #c4b5fd)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            About VTUGenX
          </h1>
          <p
            style={{
              fontSize: isMobile ? "1rem" : "1.2rem",
              opacity: 0.9,
              lineHeight: "1.6",
              color: "#cbd5e1",
            }}
          >
            Empowering VTU students with quality study materials and a
            collaborative learning community
          </p>
        </motion.div>
      </section>

      {/* Stats Section - Glassmorphism Cards */}
      <section
        style={{
          padding: isMobile ? "3rem 1.5rem" : "5rem 2rem",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: "center", marginBottom: "3rem" }}
          >
            <div
              style={{
                display: "inline-block",
                background: "rgba(139,92,246,0.1)",
                padding: "0.3rem 1rem",
                borderRadius: "50px",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  color: "#8b5cf6",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                }}
              >
                PLATFORM STATISTICS
              </span>
            </div>
            <h2
              style={{
                fontSize: isMobile ? "1.8rem" : "2.5rem",
                color: getTextColor(),
                marginBottom: "0.5rem",
              }}
            >
              Our Impact in <span style={{ color: "#8b5cf6" }}>Numbers</span>
            </h2>
            <p
              style={{
                color: getTextSecondary(),
                maxWidth: "600px",
                margin: "0 auto",
                fontSize: isMobile ? "0.9rem" : "1rem",
              }}
            >
              Real data from our growing community
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {stats_data.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -5 }}
                style={{
                  textAlign: "center",
                  padding: isMobile ? "1.5rem" : "2rem",
                  background: getCardBackground(),
                  backdropFilter: "blur(20px)",
                  borderRadius: "24px",
                  boxShadow: isDarkMode
                    ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                    : "0 25px 50px -12px rgba(0,0,0,0.25)",
                  transition: "all 0.3s",
                  position: "relative",
                  overflow: "hidden",
                  border: `1px solid ${getBorderColor()}`,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    background: stat.gradient,
                  }}
                />
                <div
                  style={{
                    background: stat.gradient,
                    width: "70px",
                    height: "70px",
                    borderRadius: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 1rem auto",
                    color: "white",
                  }}
                >
                  {stat.icon}
                </div>
                <h3
                  style={{
                    fontSize: isMobile ? "1.8rem" : "2rem",
                    background: stat.gradient,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    marginBottom: "0.3rem",
                    fontWeight: "bold",
                  }}
                >
                  {stat.value}
                </h3>
                <p
                  style={{
                    color: getTextColor(),
                    fontWeight: "600",
                    marginBottom: "0.3rem",
                  }}
                >
                  {stat.label}
                </p>
                <p style={{ color: getTextSecondary(), fontSize: "0.85rem" }}>
                  {stat.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section
        style={{
          padding: isMobile ? "3rem 1.5rem" : "5rem 2rem",
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: isMobile ? "2rem" : "4rem",
            alignItems: "center",
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div
              style={{
                display: "inline-block",
                background: "rgba(139,92,246,0.1)",
                padding: "0.3rem 1rem",
                borderRadius: "50px",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  color: "#8b5cf6",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                }}
              >
                OUR PURPOSE
              </span>
            </div>
            <h2
              style={{
                fontSize: isMobile ? "1.8rem" : "2.5rem",
                color: getTextColor(),
                marginBottom: "1.5rem",
              }}
            >
              Our <span style={{ color: "#8b5cf6" }}>Mission</span>
            </h2>
            <p
              style={{
                color: getTextSecondary(),
                lineHeight: "1.8",
                marginBottom: "1rem",
                fontSize: isMobile ? "0.9rem" : "1rem",
              }}
            >
              VTUGenX was born out of a simple idea: every student deserves
              access to quality study materials. We believe that education
              should be collaborative, and sharing knowledge benefits everyone.
            </p>
            <p
              style={{
                color: getTextSecondary(),
                lineHeight: "1.8",
                marginBottom: "2rem",
                fontSize: isMobile ? "0.9rem" : "1rem",
              }}
            >
              Our platform enables VTU students to share notes, previous year
              papers, and study resources, creating a community where learning
              is accessible to all.
            </p>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <FaCheckCircle color="#8b5cf6" size={18} />
                <span
                  style={{
                    color: getTextSecondary(),
                    fontSize: isMobile ? "0.85rem" : "0.9rem",
                  }}
                >
                  {formatNumber(stats.totalUsers)}+ Students
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <FaCheckCircle color="#8b5cf6" size={18} />
                <span
                  style={{
                    color: getTextSecondary(),
                    fontSize: isMobile ? "0.85rem" : "0.9rem",
                  }}
                >
                  {formatNumber(stats.totalNotes)}+ Resources
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <FaCheckCircle color="#8b5cf6" size={18} />
                <span
                  style={{
                    color: getTextSecondary(),
                    fontSize: isMobile ? "0.85rem" : "0.9rem",
                  }}
                >
                  100% Free Access
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              padding: isMobile ? "1.5rem" : "2.5rem",
              borderRadius: "28px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "1.5rem",
              }}
            >
              <div
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  width: "40px",
                  height: "40px",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaQuoteLeft color="white" size={18} />
              </div>
              <h3
                style={{
                  color: getTextColor(),
                  fontSize: isMobile ? "1.1rem" : "1.3rem",
                  fontWeight: "bold",
                }}
              >
                Why Choose Us?
              </h3>
            </div>
            <ul style={{ listStyle: "none", padding: 0 }}>
              {[
                { text: "Curated quality content", icon: "✓" },
                { text: "Free for all students", icon: "✓" },
                { text: "Easy to use interface", icon: "✓" },
                { text: "Community driven", icon: "✓" },
                { text: "Regular updates", icon: "✓" },
                { text: "Verified materials", icon: "✓" },
              ].map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  style={{
                    padding: "0.7rem 0",
                    color: getTextSecondary(),
                    display: "flex",
                    alignItems: "center",
                    gap: "0.8rem",
                    borderBottom:
                      index < 5 ? `1px solid ${getBorderColor()}` : "none",
                  }}
                >
                  <span
                    style={{
                      width: "24px",
                      height: "24px",
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "0.8rem",
                      fontWeight: "bold",
                    }}
                  >
                    {item.icon}
                  </span>
                  <span
                    style={{
                      color: getTextColor(),
                      fontWeight: "500",
                      fontSize: isMobile ? "0.85rem" : "0.9rem",
                    }}
                  >
                    {item.text}
                  </span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section
        style={{
          padding: isMobile ? "3rem 1.5rem" : "5rem 2rem",
          position: "relative",
          zIndex: 1,
          background: isDarkMode ? "rgba(0,0,0,0.2)" : "rgba(139,92,246,0.03)",
        }}
      >
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div
              style={{
                display: "inline-block",
                background: "rgba(139,92,246,0.1)",
                padding: "0.3rem 1rem",
                borderRadius: "50px",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  color: "#8b5cf6",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                }}
              >
                CORE VALUES
              </span>
            </div>
            <h2
              style={{
                fontSize: isMobile ? "1.8rem" : "2.5rem",
                color: getTextColor(),
                marginBottom: "1rem",
              }}
            >
              What Drives <span style={{ color: "#8b5cf6" }}>Us</span>
            </h2>
            <p
              style={{
                color: getTextSecondary(),
                marginBottom: "3rem",
                maxWidth: "600px",
                margin: "0 auto 3rem auto",
                fontSize: isMobile ? "0.9rem" : "1rem",
              }}
            >
              Our principles guide everything we do at VTUGenX
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -5 }}
                style={{
                  textAlign: "center",
                  padding: isMobile ? "1.5rem" : "2rem",
                  background: getCardBackground(),
                  backdropFilter: "blur(20px)",
                  borderRadius: "24px",
                  boxShadow: isDarkMode
                    ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                    : "0 25px 50px -12px rgba(0,0,0,0.25)",
                  border: `1px solid ${getBorderColor()}`,
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    background: value.gradient,
                    borderRadius: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 1.5rem auto",
                    color: "white",
                    fontSize: "2rem",
                  }}
                >
                  {value.icon}
                </div>
                <h3
                  style={{
                    color: getTextColor(),
                    marginBottom: "0.5rem",
                    fontWeight: "bold",
                    fontSize: isMobile ? "1.1rem" : "1.2rem",
                  }}
                >
                  {value.title}
                </h3>
                <p
                  style={{
                    color: getTextSecondary(),
                    lineHeight: "1.6",
                    fontSize: isMobile ? "0.85rem" : "0.9rem",
                  }}
                >
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;

